/**
 * The fetch layer behind sample-data mode. While `isDemoMode()` is on, every
 * request the Supabase client sends goes through `demoAwareFetch`:
 *
 * - **Auth** (`/auth/v1/…`) goes to Supabase as usual: the person stays signed in.
 * - **Company data** (`DEMO_TABLES`) is answered here, from `./fixtures.ts`,
 *   and never reaches the network.
 * - **Other reads** listed in `PASSTHROUGH_TABLES` (the company itself,
 *   members, profile, plan) go to Supabase, read-only, so the app still knows
 *   who is signed in and where. Any other table answers an empty list.
 * - **Every write is refused here**: REST inserts, updates and deletes, RPCs
 *   outside `PASSTHROUGH_RPCS`, every edge function (AI reading, matching, Fio
 *   sync, exports, e-mails) and storage. Nothing from the sample reaches the
 *   database, AI quotas, billing counts, digests or another company.
 *
 * Outside sample-data mode the real fetch runs untouched.
 */
import { buildDemoTables, type DemoTables } from "./fixtures";
import { isDemoMode } from "./mode";
import { runQuery } from "./postgrest";

/**
 * Company records, answered from the fixtures (an empty list when the sample
 * has none). Never the real company's rows.
 */
export const DEMO_TABLES: ReadonlySet<string> = new Set([
  // Payments, documents and matching
  "transactions",
  "receipts",
  "matches",
  "rejected_matches",
  "bank_accounts",
  "sync_runs",
  "bank_token_alert_log",
  "email_inbox_log",
  // Invoicing
  "invoices",
  "invoice_items",
  "invoice_bank_accounts",
  "invoice_templates",
  "recurring_invoices",
  "quotes",
  "quote_items",
  "price_list_items",
  "clients",
  "client_reminder_log",
  // Closed months: the sample company has none, the real company's stay hidden
  "accounting_periods",
  // "Nepotřebuje doklad" rules by payee: the sample has none
  "not_needed_rules",
  // Budgets, personal mode, payouts
  "business_budget_items",
  "budget_items",
  "personal_accounts",
  "personal_transactions",
  "payout_reports",
  "payout_report_items",
  // Exports and integrations
  "accountant_bundle_settings",
  "accountant_bundle_log",
  "pohoda_connections",
  "pohoda_sync_items",
  "eshop_connections",
  "eshop_invoice_links",
  "weekly_digest_log",
  "trial_activation_email_log",
  // WhatsApp intake: the sample has no phone linked and no messages
  "whatsapp_links",
  "whatsapp_link_codes",
  "whatsapp_messages",
  "whatsapp_help_log",
  // Usage of the real plan: the sample counts nothing
  "usage_counters",
  "usage_daily",
]);

/**
 * Tables read from Supabase even in sample-data mode, read-only: who is
 * signed in, which company is open and its members, the plan and billing
 * state, preferences, and platform/admin tables that hold no company records.
 * A table in neither list is answered with an empty list (fail closed), and
 * src/lib/demo/__tests__/tableCoverage.test.ts fails until it is put in one.
 */
export const PASSTHROUGH_TABLES: ReadonlySet<string> = new Set([
  // Identity and the open company
  "organizations",
  "organization_members",
  "organization_invitations",
  "organization_join_requests",
  "profiles",
  "user_preferences",
  "email_preferences",
  // The company's own category list (configuration, not records)
  "expense_categories",
  // Plan and billing of the real company
  "billing_config",
  "billing_customers",
  "billing_events",
  "billing_org_overrides",
  "billing_org_trials",
  "billing_subscription_organizations",
  "billing_subscriptions",
  "billing_trial_reminders",
  "billing_trials",
  // MCP connector grants of the signed-in person
  "mcp_oauth_clients",
  "mcp_oauth_codes",
  "mcp_oauth_tokens",
  // Security and platform tables (RLS: the person's own rows or admins only)
  "audit_logs",
  "admin_audit_log",
  "account_lockout",
  "encryption_audit",
  "platform_admins",
  "rate_limits",
  "monitoring_alert_deliveries",
  "monitoring_check_runs",
  "monitoring_checks",
  "monitoring_incidents",
  "sandbox_runs",
  "storage_cleanup_queue",
  "contact_messages",
  "feedback_notes",
  "waiting_list",
]);

/**
 * Read-only RPCs about the signed-in person and their real company (plan,
 * entitlements, member names), plus starting the trial, which a new account
 * does on its first page whether or not it looks at the sample.
 */
export const PASSTHROUGH_RPCS: ReadonlySet<string> = new Set([
  "org_plan",
  "org_entitlements",
  "my_entitlements",
  "get_team_member_emails",
  "get_join_request_requesters",
  "billing_start_trial",
]);

/** Filters that would select the real company's rows; the sample has one company. */
const IGNORED_FILTERS = ["organization_id", "user_id"];

export const DEMO_REFUSAL_CODE = "ZETTL_DEMO";

function isEnglish(): boolean {
  try {
    return window.localStorage.getItem("language") === "en";
  } catch {
    return false;
  }
}

/** The toast title for a refused action in sample-data mode. */
export function demoRefusalTitle(): string {
  return isEnglish() ? "Sample data" : "Ukázková data";
}

export function demoRefusalMessage(): string {
  return isEnglish()
    ? "This is sample data: nothing is saved or sent. Go back to your company to make changes."
    : "Toto jsou ukázková data: nic se neukládá ani neodesílá. Pro změny se vraťte do své firmy.";
}

function json(body: unknown, status: number, headers: Record<string, string> = {}): Response {
  return new Response(body === undefined ? null : JSON.stringify(body), {
    status,
    headers: { "Content-Type": "application/json", ...headers },
  });
}

function refuse(): Response {
  return json(
    { code: DEMO_REFUSAL_CODE, message: demoRefusalMessage(), details: null, hint: null },
    403
  );
}

function header(init: RequestInit | undefined, name: string): string {
  const h = init?.headers;
  if (!h) return "";
  if (h instanceof Headers) return h.get(name) ?? "";
  if (Array.isArray(h)) {
    const found = h.find(([k]) => k.toLowerCase() === name.toLowerCase());
    return found?.[1] ?? "";
  }
  const key = Object.keys(h).find((k) => k.toLowerCase() === name.toLowerCase());
  return key ? (h as Record<string, string>)[key] : "";
}

/** Answers one REST read from the sample tables, the way PostgREST would. */
export function answerRead(
  tables: DemoTables,
  table: string,
  params: URLSearchParams,
  method: string,
  accept: string
): Response {
  const query = new URLSearchParams();
  params.forEach((value, key) => {
    if (!IGNORED_FILTERS.includes(key)) query.append(key, value);
  });
  const { rows, total, offset } = runQuery(tables[table] ?? [], query);
  const range = {
    "Content-Range": rows.length ? `${offset}-${offset + rows.length - 1}/${total}` : `*/${total}`,
  };
  if (method === "HEAD") return new Response(null, { status: 200, headers: range });
  if (accept.includes("vnd.pgrst.object")) {
    if (rows.length === 1) return json(rows[0], 200, range);
    return json(
      {
        code: "PGRST116",
        message: "JSON object requested, multiple (or no) rows returned",
        details: `The result contains ${rows.length} rows`,
        hint: null,
      },
      406
    );
  }
  return json(rows, 200, range);
}

export type DemoDecision = { kind: "passthrough" } | { kind: "answer"; response: Response };

/** What sample-data mode does with one request to the Supabase project. */
export function decide(
  url: URL,
  method: string,
  init: RequestInit | undefined,
  tables: () => DemoTables
): DemoDecision {
  const path = url.pathname;
  const verb = method.toUpperCase();
  const read = verb === "GET" || verb === "HEAD";

  if (path.startsWith("/auth/v1/")) return { kind: "passthrough" };

  if (path.startsWith("/rest/v1/rpc/")) {
    const fn = path.slice("/rest/v1/rpc/".length);
    return PASSTHROUGH_RPCS.has(fn)
      ? { kind: "passthrough" }
      : { kind: "answer", response: refuse() };
  }

  if (path.startsWith("/rest/v1/")) {
    if (!read) return { kind: "answer", response: refuse() };
    const table = path.slice("/rest/v1/".length).split("/")[0];
    if (PASSTHROUGH_TABLES.has(table)) return { kind: "passthrough" };
    // DEMO_TABLES, and any table not classified yet: never the real rows.
    return {
      kind: "answer",
      response: answerRead(tables(), table, url.searchParams, verb, header(init, "Accept")),
    };
  }

  // Edge functions, storage, realtime and anything else on the project.
  return { kind: "answer", response: refuse() };
}

let cached: { day: string; tables: DemoTables } | null = null;

/** The sample, built once per day (its months end today). */
function demoTables(): DemoTables {
  const today = new Date();
  const day = today.toDateString();
  if (!cached || cached.day !== day) cached = { day, tables: buildDemoTables(today) };
  return cached.tables;
}

/**
 * The Supabase client's fetch. Requests to other hosts, and every request
 * outside sample-data mode, go to the real fetch unchanged.
 */
export function createDemoAwareFetch(
  supabaseUrl: string,
  realFetch: typeof fetch = (input, init) => fetch(input, init)
): typeof fetch {
  let origin: string | null = null;
  try {
    origin = new URL(supabaseUrl).origin;
  } catch {
    origin = null;
  }
  return async (input, init) => {
    if (!isDemoMode() || !origin) return realFetch(input, init);
    const raw = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    let url: URL;
    try {
      url = new URL(raw);
    } catch {
      return realFetch(input, init);
    }
    if (url.origin !== origin) return realFetch(input, init);
    const method = init?.method ?? (input instanceof Request ? input.method : "GET");
    const decision = decide(url, method, init, demoTables);
    return decision.kind === "passthrough" ? realFetch(input, init) : decision.response;
  };
}
